import React from "react";
import { Link } from "react-router-dom";
import { getBrand } from "../../utils/branding";
import { getLegalContent } from "../../utils/legalContent";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function Privacy() {
  const brand = getBrand();
  const c = getLegalContent();

  return (
    <div className="au-root">
      <Header />
      <div className="au-container" style={{ maxWidth: 820 }}>
        <div className="au-glass au-pad-lg" style={{ marginBottom: 56 }}>
          <div className="au-eyebrow" style={{ marginBottom: 14 }}>
            PRIVACY · LAST UPDATED {c.lastUpdated.toUpperCase()}
          </div>
          <h1 style={h1}>Privacy <span style={{ color: "var(--au-accent-ink)" }}>Policy</span></h1>
          <p className="au-lead" style={{ marginTop: 16 }}>
            Your privacy matters. Here's exactly what we collect, why, and how we protect it.
          </p>

          <Section n="1" title="Information We Collect">
            <p><strong>Account Info:</strong> Name, email, phone (optional), country</p>
            {c.kycRequired && <p><strong>KYC Documents:</strong> Aadhar/PAN/Passport for verification (encrypted, deleted after verification)</p>}
            <p><strong>Usage Data:</strong> {c.dataType}, timestamps, IP address, device info</p>
            {c.paymentSupported && <p><strong>Payment Info:</strong> Processed securely by Razorpay/Stripe (we don't store card details)</p>}
          </Section>

          <Section n="2" title="How We Use Your Data">
            <ul style={ul}>
              <li>Provide and improve the {c.fullName} Service</li>
              <li>Verify your identity {c.kycRequired ? "(KYC compliance)" : "via email verification"}</li>
              {c.paymentSupported && <li>Process payments and send receipts</li>}
              <li>Detect fraud and abuse</li>
              <li>Comply with legal obligations</li>
              <li>Send transactional emails (login codes, {c.actionVerb} reports)</li>
            </ul>
          </Section>

          <Section n="3" title="Data We Do NOT Store">
            <p style={emphasis}>
              <ShieldCheck size={16} style={icoInline} />
              {c.name} DOES NOT STORE:
            </p>
            <ul style={ul}>
              {c.kycRequired && <li>Full Aadhar images (deleted after verification — UIDAI compliant)</li>}
              {c.paymentSupported && <li>Credit card numbers (handled by payment gateway)</li>}
              <li>Target's content beyond {c.actionVerb} metadata</li>
              <li>User passwords (we use passwordless authentication)</li>
            </ul>
          </Section>

          <Section n="4" title="Data Sharing">
            <p>We DO NOT sell your data. We share only with:</p>
            <ul style={ul}>
              {c.paymentSupported && <li><strong>Payment processors</strong> (Stripe, Razorpay) — for transactions</li>}
              <li><strong>Email service</strong> (Resend) — for sending emails</li>
              <li><strong>Cloud infrastructure</strong> (AWS) — for hosting</li>
              <li><strong>Law enforcement</strong> — only when legally required</li>
            </ul>
          </Section>

          <Section n="5" title="Your Rights">
            <ul style={ul}>
              <li><strong>Access:</strong> Download all your data anytime from Settings</li>
              <li><strong>Delete:</strong> Request account deletion via {c.privacyEmail}</li>
              <li><strong>Correct:</strong> Update info from your dashboard</li>
              <li><strong>Object:</strong> Opt-out of marketing emails (transactional emails are required for service)</li>
            </ul>
          </Section>

          <Section n="6" title="Data Retention">
            <ul style={ul}>
              <li>Active accounts: Data retained while account is active</li>
              <li>Deleted accounts: Personal data removed within 30 days</li>
              {c.actionVerb === "scan" && <li>Scan reports: Stored for your account access</li>}
              <li>Logs: Anonymized server logs kept for 90 days for security</li>
            </ul>
          </Section>

          <Section n="7" title="Cookies">
            <p>We use only essential cookies for:</p>
            <ul style={ul}>
              <li>Authentication (login session)</li>
              <li>Security (CSRF protection)</li>
            </ul>
            <p>No advertising cookies. No third-party tracking.</p>
          </Section>

          <Section n="8" title="Contact Us">
            <p>Questions about your privacy? Email <a href={`mailto:${c.privacyEmail}`} style={link}>{c.privacyEmail}</a></p>
            <p style={metaMuted}>{c.fullName} · A {c.parentCompany} product · {c.address}</p>
          </Section>

          
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <div style={section}>
      <h2 style={h2}>
        <span style={{ color: "var(--au-accent-ink)", marginRight: 10 }}>{n}.</span>{title}
      </h2>
      <div style={body}>{children}</div>
    </div>
  );
}

function Header() {
  return (
    <header style={hdr}>
      <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
      <Link to="/" className="au-pill au-pill-glass au-pill-sm">
        <ArrowLeft /> Back to Home
      </Link>
    </header>
  );
}

function FooterTag({ c }) {
  return (
    <div style={copyright}>
      © 2026 {c.fullName} · A {c.parentDomain} product · Built in India
    </div>
  );
}

const hdr = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, maxWidth: 1200, margin: "0 auto", padding: "28px 24px 8px" };
const h1 = { fontSize: 42, margin: 0, letterSpacing: -1 };
const section = { marginTop: 36, paddingTop: 28, borderTop: "1px solid var(--au-hair)" };
const h2 = { fontSize: 20, marginBottom: 16, fontWeight: 700 };
const body = { color: "var(--au-text-2)", fontSize: 14, lineHeight: 1.8 };
const ul = { margin: "8px 0", paddingLeft: 22 };
const link = { color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 600 };
const emphasis = { color: "var(--au-accent-ink)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 };
const icoInline = { flex: "none" };
const metaMuted = { fontSize: 13, color: "var(--au-muted)", marginTop: 8 };
const copyright = { marginTop: 48, paddingTop: 24, borderTop: "1px solid var(--au-hair)", fontSize: 12, color: "var(--au-muted)", textAlign: "center" };
